import type { HTMLAttributes } from "react"
import { twMerge } from "tailwind-merge"

export type FieldProps = HTMLAttributes<HTMLDivElement> & {
  /** Description for the field. Should only be used if there's a `label`. */
  description?: string
  errorMessage?: string
  label?: React.ReactNode
  labelIconSlot?: React.ReactNode
}

const Label = ({ className, ...props }: HTMLAttributes<HTMLLabelElement>) => (
  <label slot="label" className={twMerge("tt-field-label", className)} {...props} />
)

const FieldError = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={twMerge("tt-field-error", className)} {...props} />
)

const Description = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={twMerge("tt-field-description", className)} {...props} />
)

export const LabelAndDescriptionStack = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={twMerge("flex flex-col gap-0.5", className)} {...props} />
)

// TextField, TextareaField

/**
 * A general-purpose component for wrapping input fields with labels,
 * descriptions, and error messages. If you're looking for an input field to use
 * out of the box, use `TextField`, `TextareaField`, or the other components in
 * ui/input.
 *
 *
 * ```tsx
 * import { Field } from "@tokenterminal/core/ui/input/Field"
 *
 * const CustomField = () => (
 *   <Field
 *     label="My custom field"
 *     description="This is a custom field"
 *     errorMessage="This field is required"
 *   >
 *     // Some input field here
 *   </Field>
 * )
 * ```
 */

export const Field = ({
  children,
  className,
  description,
  errorMessage,
  label,
  labelIconSlot,
  ...props
}: FieldProps) => {
  return (
    <div className={twMerge("tt-field-wrapper", className)} {...props}>
      {(label || description) && (
        <LabelAndDescriptionStack>
          {label && (
            <div className="flex items-center gap-2">
              <Label>{label}</Label>
              {labelIconSlot}
            </div>
          )}
          {description && <Description>{description}</Description>}
        </LabelAndDescriptionStack>
      )}
      {children}
      {errorMessage && <FieldError>{errorMessage}</FieldError>}
    </div>
  )
}
